// @ts-nocheck
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { Send, Bot } from 'lucide-react';

interface BotEdProps {
  onClose?: () => void;
}

export default function BotEd({ onClose }: BotEdProps) {
  const [messages, setMessages] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([
    { role: 'assistant', content: "Hi, I'm Bot Ed. I can help with anything about this website: navigation, auctions, lots, artists, bidding rules, account settings, etc." }
  ]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const ask = async (input: string) => {
    setLoading(true);
    setMessages((prev) => [...prev, { role: 'user', content: input }]);
    try {
      const { data, error } = await supabase.functions.invoke('ed-bot', {
        body: {
          messages: [...messages, { role: 'user', content: input }].slice(-12),
          scope: 'website'
        }
      });
      if (error) throw error;
      const content = data?.answer || 'Sorry, I could not generate an answer.';
      setMessages((prev) => [...prev, { role: 'assistant', content }]);
    } catch (e: any) {
      setMessages((prev) => [...prev, { role: 'assistant', content: e.message || 'Bot error.' }]);
    } finally {
      setLoading(false);
    }
  };

  const onSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;
    setText('');
    await ask(trimmed);
  };

  return (
    <Card className="bg-white/5 border-white/10 h-full flex flex-col">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-white">
            <div className="h-8 w-8 rounded-full bg-emerald-600/20 border border-emerald-400/40 flex items-center justify-center">
              <Bot className="h-4 w-4 text-emerald-300" />
            </div>
            <CardTitle className="text-base text-white">Bot Ed</CardTitle>
          </div>
          {onClose && (
            <Button size="sm" className="bg-white/10 border border-white/20 text-white hover:bg-white/20" onClick={onClose}>Close</Button>
          )}
        </div>
        <div className="mt-2 h-px bg-gradient-to-r from-emerald-500/50 to-teal-500/50" />
      </CardHeader>
      <CardContent className="flex-1 flex flex-col p-0 min-h-0">
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[78%] rounded-2xl px-4 py-2 border shadow-sm ${m.role === 'user' ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white border-emerald-400/40' : 'bg-white/5 text-white border-white/10'}`}>
                {m.content}
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <div className="p-3 border-t border-white/10 flex items-center gap-2">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Ask about this website..."
            className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-white/50 resize-none"
            rows={1}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); } }}
          />
          <Button disabled={loading} onClick={onSend} className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-400 hover:to-teal-400">
            <Send className="h-4 w-4 mr-1" /> Send
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
